import { Button, Grid } from '@mui/material'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { usePromiseTracker } from 'react-promise-tracker'
import { useNavigate, useParams } from 'react-router'
import ConfirmBox from 'src/components/ConfirmBox'
import SuspenseLoader from 'src/components/SuspenseLoader'
import { AdminPermissionsService } from 'src/services/adminPermissionsService'
import MasterWorkoutViewModel from 'src/viewmodels/MasterWorkoutViewModel'
import CreateOrEditMasterWorkoutForm from './CreateOrEditMasterWorkoutForm'

interface CreateEditMasterWorkoutProps {
	viewModel: MasterWorkoutViewModel
}

const CreateEditMasterWorkout: React.FC<CreateEditMasterWorkoutProps> = observer(({ viewModel }) => {
	const params = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { masterWorkout, fetch, getMasterWorkoutLookups, saveChanges, submitMasterWorkout, validateMasterWorkout, deleteMasterWorkout, lookups } = viewModel

	useEffect(() => {
		getMasterWorkoutLookups()
		if (params?.id) fetch(params.id)
	}, [params?.id])

	const handleSaveChanges = useCallback(() => {
		saveChanges().then((id) => {
			// Keep page opened
			if (masterWorkout.id > 0) fetch(`${id}`)
			// Navigate to id and update params.id
			else navigate(`/master-workouts/${id}`)
		})
	}, [masterWorkout.id])

	const { canCreate, canDelete, verifyEdit } = AdminPermissionsService.instance.masterWorkoutPage
	const canEdit = (_.isNil(masterWorkout.id) && canCreate) || verifyEdit(masterWorkout.isPublic)

	const [confirmBoxTitle, setConfirmBoxTitle] = useState<string>('')

	const handleDelete = useCallback(() => {
		setConfirmBoxTitle('Delete Master Workout')
	}, [])

	const onConfirmDelete = useCallback(() => {
		deleteMasterWorkout().then(() => {
			setConfirmBoxTitle('')
			navigate(`/master-workouts`)
		})
	}, [])

	const { promiseInProgress } = usePromiseTracker()

	return promiseInProgress ? (
		<SuspenseLoader />
	) : (
		<>
			<Grid container spacing={3} mt={1}>
				<Grid item xs={12} display="flex" justifyContent="end" alignItems="center">
					{canEdit && (
						<Button sx={{ mt: { xs: 2, md: 0 }, mx: 1 }} variant="contained" onClick={handleSaveChanges}>
							Save Changes
						</Button>
					)}
				</Grid>

				<Grid item xs={12}>
					{masterWorkout && (
						<CreateOrEditMasterWorkoutForm initialValues={masterWorkout} lookups={lookups} onSubmit={submitMasterWorkout} onValidate={validateMasterWorkout} />
					)}
				</Grid>

				{canDelete && (
					<Grid display="flex" item xs={12} justifyContent="center">
						<Button fullWidth variant="contained" color="error" onClick={handleDelete}>
							Delete
						</Button>
					</Grid>
				)}
			</Grid>
			<ConfirmBox onCancel={() => setConfirmBoxTitle('')} onConfirm={onConfirmDelete} title={confirmBoxTitle} />
		</>
	)
})

export default CreateEditMasterWorkout
